import { reactive, ref, toRefs, nextTick, computed } from 'vue'
import { AUDIENCES_TYPE } from '@/views/create/gdt/model/targeting-const.js'
import { BRAND_ENUM, MATERIAL_SIZES } from '@/views/create/gdt/model/creative-const.js'
import { appendMaterialImageList } from '@/views/create/gdt/template/hooks/useGdtPlansPreview.js'
import { useIconSelector } from '../../../hooks/useGdtCreativeForm'
import { Message } from 'view-design'
import { clone, groupBy } from '@/common/js/just.js'
import { getAccountById } from '@/api/ptom.data.model.js'
import { MATERIAL_TYPE_ID, MATERIAL_TYPE } from '@/components/material-selector/consts.js'

export const getDefaultCheckStates = () => {
  return {
    accountCheck: false, // 广告账号勾选
    creativeCheck: false, // 创意组勾选
    dailyBudgetCheck: false, // 每日预算勾选
    targetingCheck: false,
    totalBudgetCheck: false, // 总预算勾选
    bidCheck: false, // 出价勾选
    convertCheck: false, // 转化勾选
    populationCheck: false, // 人群包勾选
    pagesCheck: false, // 落地页
    pagesV3Check: false, // 3.0落地页
    profileCheck: false, // 头像昵称及跳转页
    wxgamePlayableCheck: false, // 小游戏试玩页
    wxgamePlayableEndPageCheck: false, // 试玩结束页
    descriptionCheck: false // 文案包
  }
}
export const getDefaultModalStates = () => {
  return {
    showGlobalAccountModify: false,
    showGlobalTargetModify: false,
    showGlobalCreativeModify: false,
    showGlobalBudgetModify: false,
    showGlobalTotalBudgetModify: false,
    showGlobalBidModify: false,
    showGlobalConvertModify: false,
    showGlobalPopModify: false,
    showGlobalPagesModify: false,
    showGlobalPagesV3Modify: false,
    showGlobalProfileModify: false,
    showGlobalWxgamePlayableModify: false,
    showGlobalDescriptionModify: false
  }
}

const globalModal = ref(getDefaultModalStates())
const tableHeaderForm = ref(getDefaultCheckStates())
const currentModifyTableRow = ref(null)

export const useStates = (tableData, emits) => {
  function onGlobalCheckChange(isCheckAll, prop) {
    const newData = tableData.value.map((row) => {
      return { ...row, [prop]: isCheckAll }
    })

    emits('update-table-data', newData)
  }

  function onPreviewTableSelectionChange(selection) {
    console.log('selection output: ', selection)
  }
  return {
    globalModal,
    tableHeaderForm,
    currentModifyTableRow,
    onGlobalCheckChange,
    onPreviewTableSelectionChange
  }
}

/**
 * @description: 修改日预算
 * @param {*} tableData
 * @param {*} emits
 * @return {*}
 */
export const useBudget = (tableData, emits, onModifyModalCancel) => {
  function onBudgetModify(rowIndex) {
    const hasRowIndex = !Object.is(Number(rowIndex), NaN)
    if (!hasRowIndex) {
      if (!tableData.value.some((row) => row.dailyBudgetCheck)) {
        return Message.warning('请选择修改行')
      }
    }

    const planNames = hasRowIndex ? [tableData.value[rowIndex].plan.project_name] : tableData.value.filter(row => row.dailyBudgetCheck).map(row => row.plan.project_name)
    const newData = tableData.value.map((row) => {
      if (planNames.includes(row.plan.project_name)) {
        return { ...row, dailyBudgetCheck: true }
      }
      return row
    })
    emits('update-table-data', newData)

    // 保存当前需要更改的行，如果是全选更改，那么保存第一个
    const row = tableData.value[rowIndex || 0]
    currentModifyTableRow.value = clone(row)
    globalModal.value.showGlobalBudgetModify = true
  }

  function onBudgetModifyConfirm(budgetForm) {
    const { budget, daily_budget } = budgetForm

    const newData = tableData.value.map((row) => {
      if (row.dailyBudgetCheck) {
        const newRow = { ...row, bid: { ...row.bid, budget, daily_budget } }
        return newRow
      }
      return row
    })
    emits('update-table-data', newData)
    onModifyModalCancel()
  }

  return {
    onBudgetModify,
    onBudgetModifyConfirm
  }
}

/**
 * @description: 修改定向包
 * @param {*} tableData
 * @param {*} emits
 * @return {*}
 */
export const useTargeting = (tableData, emits, onModifyModalCancel) => {
  function onTargetingModify(rowIndex) {
    const hasRowIndex = !Object.is(Number(rowIndex), NaN)
    if (!hasRowIndex) {
      if (!tableData.value.some((row) => row.targetingCheck)) {
        return Message.warning('请选择修改行')
      }
    }

    const planNames = hasRowIndex ? [tableData.value[rowIndex].plan.project_name] : tableData.value.filter(row => row.targetingCheck).map(row => row.plan.project_name)
    const newData = tableData.value.map((row) => {
      if (planNames.includes(row.plan.project_name)) {
        return { ...row, targetingCheck: true }
      }
      return row
    })
    emits('update-table-data', newData)

    // 保存当前需要更改的行，如果是全选更改，那么保存第一个
    const row = tableData.value[rowIndex || 0]
    currentModifyTableRow.value = clone(row)
    globalModal.value.showGlobalTargetModify = true
  }

  function onTargetingModifyConfirm(targetItem) {
    const newData = tableData.value.map((row) => {
      if (row.targetingCheck) {
        const newRow = { ...row, target: { ...targetItem } }
        return newRow
      }
      return row
    })
    emits('update-table-data', newData)
    onModifyModalCancel()
  }

  return {
    onTargetingModify,
    onTargetingModifyConfirm
  }
}

export const useConvert = (tableData, emits, onModifyModalCancel) => {
  function onConvertModify(rowIndex) {
    const hasRowIndex = !Object.is(Number(rowIndex), NaN)
    if (!hasRowIndex) {
      if (!tableData.value.some((row) => row.convertCheck)) {
        return Message.warning('请选择修改行')
      }
    }

    const planNames = hasRowIndex ? [tableData.value[rowIndex].plan.project_name] : tableData.value.filter(row => row.convertCheck).map(row => row.plan.project_name)
    const newData = tableData.value.map((row) => {
      if (planNames.includes(row.plan.project_name)) {
        return { ...row, convertCheck: true }
      }
      return row
    })
    emits('update-table-data', newData)

    const checkedIndex = tableData.value.findIndex((row) => row.convertCheck)
    // 保存当前需要更改的行，如果是点击的table head 栏的修改，找到第一个勾选的作为当前修改行
    const row = tableData.value[rowIndex || Math.max(checkedIndex, 0)]
    currentModifyTableRow.value = clone(row)
    globalModal.value.showGlobalConvertModify = true
  }

  function onConvertModifyConfirm(convertForm) {
    if (convertForm) {
      const newData = tableData.value.map((row) => {
        if (row.convertCheck) {
          const newRow = clone(row)
          newRow.bid = { ...newRow.bid, ...convertForm }
          return newRow
        }

        return row
      })

      emits('update-table-data', newData)
      onModifyModalCancel()
    }
  }

  return {
    onConvertModify,
    onConvertModifyConfirm
  }
}

export const useAudience = (tableData, emits, onModifyModalCancel) => {
  function hasAudiences(row) {
    const { populations } = row.target
    const accountId = row.ad_account_id
    if (populations) {
      const { includes, excludes } = populations[accountId] || {}
      return includes?.length || excludes?.length
    }
    return false
  }

  async function onAudienceModify(rowIndex) {
    // 点击单行时设置check状态
    if (!Object.is(Number(rowIndex), NaN)) {
      const newData = tableData.value.map((row, index) => {
        return { ...row, populationCheck: index === rowIndex }
      })

      emits('update-table-data', newData)
    } else {
      if (!tableData.value.some((row) => row.populationCheck)) {
        return Message.warning('请选择修改行')
      }
    }

    // 保存当前需要更改的行，如果是全选更改，那么保存第一个
    const row = tableData.value[rowIndex || 0]
    const { id, adAccount } = await getAccountById({ ad_account_id: row.ad_account_id })
    row.plan.account = { id, name: adAccount }
    currentModifyTableRow.value = clone(row)
    globalModal.value.showGlobalPopModify = true
  }

  function onAudienceModifyConfirm(audiences) {
    const populations = audiences[currentModifyTableRow.value.ad_account_id]
    if (populations) {
      const newData = tableData.value.map((row) => {
        if (row.populationCheck) {
          const newRow = clone(row)
          newRow.target.populations = {
            [populations.accountId]: populations
          }
          newRow.target.audienceType = AUDIENCES_TYPE.LIMITED
          return newRow
        }

        return row
      })
      emits('update-table-data', newData)
    }

    onModifyModalCancel()
  }

  return {
    hasAudiences,
    onAudienceModify,
    onAudienceModifyConfirm
  }
}

export const useProfile = (tableData, emits, onModifyModalCancel) => {
  function getV3ProfileName(row) {
    const { brandInfo, profileInfo, officialAccountInfo } = row.creative.components.brand || {}
    const { channelInfo } = row.creative.components.wechat_channels || {}
    const { brand_id, name } = brandInfo || {}
    const { profile_id, profile_name } = profileInfo || {}
    const { promoted_object_id, promoted_object_name } = channelInfo || {}
    const { promoted_object_id: official_account_promoted_object_id, promoted_object_name: official_account_promoted_object_name } =
      officialAccountInfo || {}

    if (brand_id) {
      return `品牌形象：${name}`
    }

    if (profile_id) {
      return `品牌简介页：${profile_name}`
    }

    if (promoted_object_id) {
      return `视频号：${promoted_object_name}`
    }

    if (official_account_promoted_object_id) {
      return `公众号：${official_account_promoted_object_name}`
    }
    return '未配置品牌形象跳转'
  }

  function onProfileModify(rowIndex) {
    // 点击单行时设置check状态
    if (!Object.is(Number(rowIndex), NaN)) {
      const newData = tableData.value.map((row, index) => {
        return { ...row, profileCheck: index === rowIndex }
      })

      emits('update-table-data', newData)
    } else {
      if (!tableData.value.some((row) => row.profileCheck)) {
        return Message.warning('请选择修改行')
      }
    }

    // 保存当前需要更改的行，如果是全选更改，那么保存第一个
    const row = tableData.value[rowIndex || 0]
    // 使用分配的账号id
    row.plan.account = { id: row.ad_account_id }
    currentModifyTableRow.value = clone(row)
    globalModal.value.showGlobalProfileModify = true
  }

  function onProfileModifyConfirm(info, brandType) {
    const { brand_id, image_id, profile_id, promoted_object_id } = info
    const newTableDate = tableData.value.map((row) => {
      if (row.profileCheck) {
        const newRow = clone(row)
        newRow.creative.components.brand.profileInfo = {}
        newRow.creative.components.brand.brandInfo = {}
        newRow.creative.components.brand.officialAccountInfo = {}
        newRow.creative.components.wechat_channels.channelInfo = {}
        return newRow
      }
      return row
    })

    // 只有3.0有品牌形象
    if (brand_id && image_id) {
      const newData = newTableDate.map((row) => {
        if (row.profileCheck) {
          const newRow = clone(row)
          newRow.creative.components.brand.brandInfo = info
          newRow.creative.components.brand.brandType = brandType
          return newRow
        }
        return row
      })

      emits('update-table-data', newData)
    }
    if (promoted_object_id) {
      const newData = newTableDate.map((row) => {
        if (row.profileCheck) {
          const newRow = clone(row)

          // 视频号
          if (brandType === BRAND_ENUM.CHANNEL) {
            newRow.creative.components.wechat_channels.channelInfo = info
          } else if (brandType === BRAND_ENUM.OFFICIAL_ACCOUNT) {
            // 公众号
            newRow.creative.components.brand.officialAccountInfo = info
          }
          newRow.creative.components.brand.brandType = brandType
          return newRow
        }
        return row
      })
      emits('update-table-data', newData)
    }
    if (!brand_id && profile_id) {
      const newData = newTableDate.map((row) => {
        if (row.profileCheck) {
          const newRow = clone(row)

          newRow.creative.components.brand.profileInfo = info
          newRow.creative.components.brand.brandType = brandType
          return newRow
        }
        return row
      })
      emits('update-table-data', newData)
    }

    onModifyModalCancel()
  }

  return {
    getV3ProfileName,
    onProfileModify,
    onProfileModifyConfirm
  }
}

export const useLandingPages = (tableData, emits, onModifyModalCancel) => {
  function onPagesV3Modify(rowIndex) {
    if (!Object.is(Number(rowIndex), NaN)) {
      const newData = tableData.value.map((row, index) => {
        return { ...row, pagesV3Check: index === rowIndex }
      })
      emits('update-table-data', newData)
    } else {
      if (!tableData.value.some((row) => row.pagesV3Check)) {
        return Message.warning('请选择修改行')
      }
    }
    const row = tableData.value[rowIndex || 0]
    // 使用分配的账号id
    row.plan.account = { id: row.ad_account_id }
    currentModifyTableRow.value = clone(row)
    globalModal.value.showGlobalPagesV3Modify = true
  }

  function onPagesV3ModifyConfirm(page) {
    if (page) {
      const newData = tableData.value.map((row) => {
        if (row.pagesV3Check) {
          const newRow = clone(row)
          newRow.creative.pageInfo = page
          return newRow
        }
        return row
      })

      emits('update-table-data', newData)
    }
    onModifyModalCancel()
  }

  return {
    onPagesV3Modify,
    onPagesV3ModifyConfirm
  }
}

export const useCreatives = (tableData, emits, onModifyModalCancel) => {
  function onCreativeModify(rowIndex) {
    if (!Object.is(Number(rowIndex), NaN)) {
      const newData = tableData.value.map((row, index) => {
        return { ...row, creativeCheck: index === rowIndex }
      })
      emits('update-table-data', newData)
    } else {
      if (!tableData.value.some((row) => row.creativeCheck)) {
        return Message.warning('请选择修改行')
      }
    }
    const row = tableData.value[rowIndex || 0]
    // 使用分配的账号id
    row.plan.account = { id: row.ad_account_id }
    currentModifyTableRow.value = clone(row)
    globalModal.value.showGlobalCreativeModify = true
  }

  function onCreativeModifyConfirm(creative) {
    if (creative) {
      const newData = tableData.value.map((row) => {
        if (row.creativeCheck) {
          const newRow = clone(row)
          const { helper } = appendMaterialImageList(newRow, creative)
          // 更改提交参数
          newRow.creative.groupName = creative.creative_name
          newRow.creative.selectedAssets = creative.selectedMaterials
          // 更改预览参数
          newRow.helper.preview_list = helper.preview_list
          return newRow
        }
        return row
      })
      emits('update-table-data', newData)
    }
    onModifyModalCancel()
  }

  return {
    onCreativeModify,
    onCreativeModifyConfirm
  }
}

export const useTableSpanMethods = (tableData) => {
  /**
   * @description: 表格合并单元格辅助计算属性
   */
  const tableGroups = computed(() => {
    const groups = {}
    tableData.value.forEach((row, rowIndex) => {
      const tempName = row.permanent_plan_name
      const group = groups[tempName]
      if (!group) {
        groups[tempName] = {
          rowIndex,
          num: 1
        }
      } else {
        groups[tempName] = Object.assign({}, group, {
          num: group.num + 1
        })
      }
    })
    return groups
  })

  /**
   * @description: 表格合并
   */
  function tableSpanMethod({ row, column, rowIndex, columnIndex }) {
    const group = tableGroups.value[row.permanent_plan_name]
    if ([0, 1, 2, 3].includes(columnIndex)) {
      if (rowIndex === group.rowIndex) {
        return {
          // group.num 在computed: tableGroups中计算得出
          rowspan: group.num,
          colspan: 1
        }
      } else {
        return {
          rowspan: 0,
          colspan: 0
        }
      }
    }
  }
  return {
    tableGroups,
    tableSpanMethod
  }
}

export const useTableCheck = (tableData, tableForm, emits) => {
  function onAdGlobalCheckChange(e) {
    const newData = tableData.value.map((row) => {
      row.helper.isCheck = e
      return row
    })

    emits('update-table-data', newData)
  }

  function onTableRowCheckChange(e, index) {
    const isAllCheck = tableData.value.every((row) => {
      return row.helper.isCheck
    })

    emits('update-table-form', {
      ...tableForm.value,
      isCheckAll: isAllCheck
    })
  }
  return { onAdGlobalCheckChange, onTableRowCheckChange }
}

export const useWxgamePlayablePage = (tableData, emits, onModifyModalCancel) => {
  const { selectorModal, selectorProps, showIconSelector, onSelectorClose } = useIconSelector()
  const wxgamePlayableSelectorProps = ref({
    ...selectorProps.value,
    assetType: MATERIAL_TYPE_ID[MATERIAL_TYPE.IMAGE],
    form: { ...selectorProps.value.form, size: MATERIAL_SIZES.SIZE_1280_720 }
  })
  function onWxgamePlayableModify(rowIndex) {
    if (!Object.is(Number(rowIndex), NaN)) {
      const newData = tableData.value.map((row, index) => {
        return { ...row, wxgamePlayableCheck: index === rowIndex }
      })
      emits('update-table-data', newData)
    } else {
      if (!tableData.value.some((row) => row.wxgamePlayableCheck)) {
        return Message.warning('请选择修改行')
      }
    }
    const row = tableData.value[rowIndex || 0]
    // 使用分配的账号id
    row.plan.account = { id: row.ad_account_id }
    currentModifyTableRow.value = clone(row)
    globalModal.value.showGlobalWxgamePlayableModify = true
  }

  function getWxgamePlayablePageDisplayText(row) {
    const { playable_item } = row.creative.components.wxgame_playable_material_config || {}
    return playable_item?.playable_page_name || '-'
  }

  function onWxgamePlayableModifyConfirm(selectedPage) {
    if (selectedPage) {
      const newData = tableData.value.map((row) => {
        if (row.wxgamePlayableCheck) {
          const newRow = clone(row)
          newRow.creative.components.wxgame_playable_material_config.playable_item = selectedPage
          return newRow
        }
        return row
      })
      emits('update-table-data', newData)
    }
    onModifyModalCancel()
  }

  function onWxgamePlayableEndPageModify(rowIndex) {
    if (!Object.is(Number(rowIndex), NaN)) {
      const newData = tableData.value.map((row, index) => {
        return { ...row, wxgamePlayableEndPageCheck: index === rowIndex }
      })
      emits('update-table-data', newData)
    } else {
      if (!tableData.value.some((row) => row.wxgamePlayableEndPageCheck)) {
        return Message.warning('请选择修改行')
      }
    }
    showIconSelector()
  }

  function onWxgamePlayableEndPageModifyConfirm(assets) {
    if (assets.length) {
      const [img] = assets
      const newData = tableData.value.map((row) => {
        if (row.wxgamePlayableEndPageCheck) {
          const newRow = clone(row)
          // 替换素材组选中的图片和预览表格的预览图片
          newRow.creative.selectedAssets = { image: [img], vodeo: [], image_list: [] }
          newRow.helper.preview_list = [{
            // 添加地址和横版竖版信息，用于表格显示
            name: img.name,
            badge: '图片',
            url: img.preview_url,
            vertical: img.height > img.width
          }]
          return newRow
        }
        return row
      })
      emits('update-table-data', newData)
    }
    onSelectorClose()
  }

  return {
    wxgamePlayableSelectorModal: selectorModal,
    wxgamePlayableSelectorProps,
    getWxgamePlayablePageDisplayText,
    onWxgamePlayableModify,
    onWxgamePlayableModifyConfirm,
    onWxgamePlayableEndPageModify,
    onWxgamePlayableEndPageModifyConfirm
  }
}

export const useDescriptions = (isWxGamePlayable, tableData, emits, onModifyModalCancel) => {
  function onDescriptionModify(rowIndex) {
    if (!Object.is(Number(rowIndex), NaN)) {
      const newData = tableData.value.map((row, index) => {
        return { ...row, descriptionCheck: index === rowIndex }
      })
      emits('update-table-data', newData)
    } else {
      if (!tableData.value.some((row) => row.descriptionCheck)) {
        return Message.warning('请选择修改行')
      }
    }
    globalModal.value.showGlobalDescriptionModify = true
  }

  function onDescriptionModifyConfirm([desc]) {
    if (desc) {
      const newData = tableData.value.map((row) => {
        if (row.descriptionCheck) {
          const newRow = clone(row)
          newRow.helper.title_pack_name = desc.packName
          newRow.creative.description = desc.content.slice(0, 3)
          return newRow
        }
        return row
      })
      emits('update-table-data', newData)
    }
    onModifyModalCancel()
  }

  function onTitleSelectorBeforeConfirm(descs) {
    if (isWxGamePlayable.value) {
      if (descs[0].content.length > 1) {
        return Promise.reject(new Error('试玩结束页只能选择文案个数等于1的文案包'))
      }
    }

    return Promise.resolve()
  }

  return {
    onDescriptionModify, onDescriptionModifyConfirm, onTitleSelectorBeforeConfirm
  }
}
